import { useEffect, useState } from "react";

import { AlertV4, ButtonV4, ModalV4 } from "../common/ui";
import {
  packageDraftOf,
  packageErrorOf,
  packageSumOf,
  type InvoiceItem,
  type PackageApplyRow,
  type PackageDraft,
} from "./billingModelV4";

// Types
type ModalBillingPackageV4Props = {
  // data
  item: InvoiceItem;
  rows: PackageApplyRow[];
  loading?: boolean;
  saving?: boolean;
  error?: unknown;
  // callback
  onSave: (draft: PackageDraft) => void;
  onCloseError: () => void;
  onClose: () => void;
};

/** pop-up เลือก Package ที่ตัดใช้ ของรายการค่าใช้จ่าย 1 แถว */
const ModalBillingPackageV4 = (props: ModalBillingPackageV4Props) => {
  const [draft, setDraft] = useState<PackageDraft>({});
  const [error, setError] = useState("");

  const quantity = Number(props.item.quantity) || 0;

  // Effect
  useEffect(() => {
    setDraft(packageDraftOf(props.rows));
    setError("");
  }, [props.rows]);

  // Handler
  const handleChange = (id: number, value: string) => {
    setDraft({ ...draft, [id]: value });
  };

  const handleClear = () => {
    const cleared: PackageDraft = {};

    for (const row of props.rows) {
      cleared[row.package_order_item_id] = "0";
    }

    setDraft(cleared);
    setError("");
    props.onSave(cleared);
  };

  const handleConfirm = () => {
    const message = packageErrorOf(props.rows, draft, quantity);

    setError(message);

    if (!message) {
      props.onSave(draft);
    }
  };

  // Render
  return (
    <ModalV4 title={props.item.product ?? ""} width={820} onClose={props.onClose}>
      <p className="hv4-bill-pkg-desc">
        {props.item.code} · {props.item.product} · จำนวน {quantity} · เลือกว่าจะตัดใช้จาก Package
        ไหน หรือใส่ 0 ทุกช่องถ้าไม่ใช้ Package
      </p>
      <div className="hv4-bill-pkg-wrap">
        <div className="hv4-bill-pkg-grid hv4-bill-pkg-th">
          <span>Package</span>
          <span>จำนวนทั้งหมด</span>
          <span>คงเหลือก่อนใช้</span>
          <span>นำมาใช้</span>
          <span>คงเหลือหลังใช้</span>
        </div>
        {props.loading && <div className="hv4-bill-pkg-empty">กำลังโหลด Package</div>}
        {props.rows.map((row) => {
          const id = row.package_order_item_id;
          const remain = Number(row.quantity_left) || 0;
          const value = draft[id] ?? "0";
          const used = Number(value) || 0;
          const applying = used > 0 && used <= remain;

          return (
            <div key={id} className="hv4-bill-pkg-grid hv4-bill-pkg-row">
              <span>
                <b>{row.package_name}</b>
                <span className="hv4-bill-pkg-code">{row.package_code}</span>
              </span>
              <span className="hv4-bill-num-c">{(Number(row.quantity_total) || 0).toFixed(2)}</span>
              <span className="hv4-bill-num-c">{remain.toFixed(2)}</span>
              <span className="hv4-bill-num-c">
                <input
                  type="number"
                  min={0}
                  max={remain}
                  className="hv4-bill-pkg-in"
                  aria-label={`นำมาใช้จาก ${row.package_name}`}
                  aria-invalid={used < 0 || used > remain || undefined}
                  disabled={remain === 0 && used === 0}
                  value={value}
                  onChange={(e) => handleChange(id, e.target.value)}
                />
              </span>
              <b className={`hv4-bill-num-c${applying ? " hv4-bill-pkg-left--on" : ""}`}>
                {remain - used}
              </b>
            </div>
          );
        })}
      </div>
      <AlertV4 tone="danger" error={props.error} onClose={props.onCloseError} />
      {!!error && (
        <div role="alert" className="hv4-bill-pkg-err">
          {error}
        </div>
      )}
      <div className="hv4-bill-pkg-foot">
        <span className="hv4-bill-pkg-sum">
          นำมาใช้รวม {packageSumOf(draft)} / {quantity}
        </span>
        <ButtonV4 variant="ghost" disabled={props.saving} onClick={handleClear}>
          ไม่ใช้ Package
        </ButtonV4>
        <ButtonV4 variant="ok" disabled={props.saving || props.loading} onClick={handleConfirm}>
          ยืนยัน
        </ButtonV4>
        <ButtonV4 variant="danger" onClick={props.onClose}>
          ปิด
        </ButtonV4>
      </div>
    </ModalV4>
  );
};

ModalBillingPackageV4.displayName = "ModalBillingPackageV4";

export default ModalBillingPackageV4;
